import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const justLoggedIn = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  // Verify token only on initial mount (existing localStorage token)
  useEffect(() => {
    if (justLoggedIn.current) {
      justLoggedIn.current = false;
      return;
    }

    const verifyToken = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch('/verify-admin.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.valid) {
            setIsAuthenticated(true);
          } else {
            logout();
          }
        } else {
          logout();
        }
      } catch {
        logout();
      } finally {
        setIsLoading(false);
      }
    };
    verifyToken();
  }, [token, logout]);

  const login = async (password) => {
    const res = await fetch('/admin-auth.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      localStorage.setItem('admin_token', data.token);
      justLoggedIn.current = true;
      setToken(data.token);
      setIsAuthenticated(true);
      setIsLoading(false);
      return { success: true };
    }
    return { success: false, error: data.error || 'Authentication failed' };
  };

  return (
    <AdminAuthContext.Provider value={{ token, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};
